// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Favourites  (shared by pages/Favourites, pages/ProductPage and hooks/usePackSheet → the heart on each card)
// The signed-in customer's saved catches, loaded once per screen and updated optimistically: a heart flips at once and is
// put back (with `error` set) if the server refuses. Signed-out visitors get `available: false` — no heart is shown.
// ─────────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useState } from "react";
import type { CutId } from "../data/cuts";
import type { PackId } from "../data/packs";
import { ApiError } from "../utils/api";
import { fetchFavouriteCatches, removeFavouriteCatch, saveFavouriteCatch, type FavouriteCatch } from "../utils/favouriteCatches";
import useAuth from "./useAuth";

type Choice = { cut?: CutId | null; pack?: PackId | null };

export default function useFavouriteCatches() {
  const signedIn = useAuth().status === "signed-in";
  const [rows, setRows] = useState<FavouriteCatch[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setFailed(false);
    try {
      setRows(await fetchFavouriteCatches());
    } catch {
      setFailed(true);
    }
  }, []);

  useEffect(() => {
    if (signedIn) void load();
  }, [signedIn, load]);

  const has = (id: string) => rows?.some((row) => row.catchId === id) ?? false;

  /** Save (or update the cut/pack of) a catch. */
  const save = async (id: string, choice: Choice = {}) => {
    const previous = rows;
    setError(null);
    const optimistic: FavouriteCatch = { catchId: id, cut: choice.cut ?? null, pack: choice.pack ?? null, createdAt: new Date().toISOString() };
    setRows((current) => [optimistic, ...(current ?? []).filter((row) => row.catchId !== id)]);
    try {
      const saved = await saveFavouriteCatch(id, choice);
      setRows((current) => (current ?? []).map((row) => (row.catchId === id ? saved : row)));
    } catch (caught) {
      setRows(previous);
      setError(caught instanceof ApiError ? caught.message : "We couldn't save that. Please try again.");
    }
  };

  const remove = async (id: string) => {
    const previous = rows;
    setError(null);
    setRows((current) => (current ?? []).filter((row) => row.catchId !== id));
    try {
      await removeFavouriteCatch(id);
    } catch {
      setRows(previous);
      setError("We couldn't remove that. Please try again.");
    }
  };

  return {
    available: signedIn,
    /** null while loading. */
    rows,
    failed,
    error,
    reload: load,
    has,
    save,
    remove,
    toggle: (id: string, choice: Choice = {}) => (has(id) ? remove(id) : save(id, choice)),
  };
}
